import Marker, { MarkerProps } from 'components/elements/maps/Marker'
import {
  BOUNDS_AMOUNT_TO_SHIFT,
  CoordsProps,
  MAP_LANGUAGE,
  MAP_OPTIONS,
  MAP_REGION,
  MARKER_BOUNDS_MARGIN,
  TRACKING_MAP_DEFAULT_ZOOM
} from 'constants/map'
import { TRACKING_MAP_HEIGHT } from 'constants/size'
import { APIKeyContext } from 'contexts/APIKeyContext'
import GoogleMapReact, { fitBounds } from 'google-map-react'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

/**
 * 位置情報共有用マップ
 */
const TrackingMap: React.FC<TrackingMapProps> = (props) => {
  const [apiKey] = useContext(APIKeyContext)
  const [center, setCenter] = useState(props.defaultCenter)
  const [zoom, setZoom] = useState(props.zoom || TRACKING_MAP_DEFAULT_ZOOM)

  // 表示範囲とズームを調整して、全てのマーカーが表示されるようにする
  const fitBoundsMap = (): void => {
    // マーカーが2つ以上のときのみ機能する
    if (!props.markers || props.markers.length < 2) {
      return
    }

    const existLats = props.markers.map((marker) => marker.lat)
    const existLngs = props.markers.map((marker) => marker.lng)

    // 北緯、東経の範囲内のみ有効
    const edgeCoords = {
      n: Math.max(...existLats),
      s: Math.min(...existLats),
      e: Math.max(...existLngs),
      w: Math.min(...existLngs),
    }

    const bounds: GoogleMapReact.NESWBounds = {
      ne: {
        lat: edgeCoords.n,
        // fitBoundsはneとswが同座標のものに対応していないため、
        // 見た目に影響のない分だけずらす
        lng: edgeCoords.e + BOUNDS_AMOUNT_TO_SHIFT,
      },
      sw: {
        // fitBoundsはneとswが同座標のものに対応していないため、
        // 見た目に影響のない分だけずらす
        lat: edgeCoords.s + BOUNDS_AMOUNT_TO_SHIFT,
        lng: edgeCoords.w,
      },
    }

    const { innerWidth: width } = window
    const size: GoogleMapReact.Size = {
      width: width - MARKER_BOUNDS_MARGIN,
      height: parseInt(TRACKING_MAP_HEIGHT) - MARKER_BOUNDS_MARGIN,
    }

    const boundsResult = fitBounds(bounds, size)
    setCenter(boundsResult.center)
    setZoom(boundsResult.zoom)
  }

  const markerDeps = props.markers
    ?.map((marker) => [marker.lat, marker.lng])
    .flat()
  useEffect(fitBoundsMap, markerDeps)

  // マップの移動等を追跡してstateを更新
  // 要請者がズーム率変更後、fitBoundsMapのズーム率適用が動作しない問題対応
  const onMapChange = useCallback(
    ({ center, zoom }: { center: CoordsProps; zoom: number }): void => {
      setCenter(center)
      setZoom(zoom)
    },
    []
  )

  return (
    <React.Fragment>
      <StyledMapArea height={props.height}>
        {apiKey && (
          <GoogleMapReact
            bootstrapURLKeys={{
              key: apiKey,
              language: MAP_LANGUAGE,
              region: MAP_REGION,
              libraries: 'places',
            }}
            onChange={onMapChange}
            draggable={!props.fixed}
            options={{
              ...MAP_OPTIONS,
              ...props.options,
              fullscreenControl: !props.fixed && !!props.fullscreenControl,
              zoomControl: !props.fixed,
            }}
            defaultCenter={props.defaultCenter}
            center={center}
            zoom={zoom}
            yesIWantToUseGoogleMapApiInternals
          >
            {props.markers &&
              props.markers.map((marker, index) => {
                return <Marker {...marker} key={index} />
              })}
          </GoogleMapReact>
        )}
      </StyledMapArea>
    </React.Fragment>
  )
}

export type TrackingMapProps = {
  height?: string
  zoom?: number
  defaultCenter: CoordsProps
  markers?: Array<MarkerProps>
  fixed?: boolean
  fullscreenControl?: boolean
  options?: GoogleMapReact.MapOptions
}

const StyledMapArea = styled.div<{
  height?: string
}>`
  width: 100%;
  height: ${(props): string => (props.height ? props.height : '100vh')};
  transition: all 1s ease-in-out;
`

export default TrackingMap
